import React from "react";
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
} from "@mui/material";

import { brand } from "../../theme/brand";

import type { SxProps, Theme } from "@mui/material/styles";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "neutral"
  | "ghost"
  | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends Omit<
  MuiButtonProps,
  "variant" | "size" | "color"
> {
  /** Visual style variant. Default: 'primary' */
  variant?: ButtonVariant;
  /** Button size. Default: 'md' */
  size?: ButtonSize;
  /** Loading state — disables button + shows spinner */
  loading?: boolean;
}

/**
 * The brand variants take their colour from `palette.brand` via `sx` rather
 * than a MUI `color`, because `primary` and `secondary` mean opposite things in
 * this theme and the app's. `inherit` keeps MUI from painting a palette colour
 * underneath what `buildOverrideSx` paints on top.
 */
const variantMap: Record<
  ButtonVariant,
  { variant: MuiButtonProps["variant"]; color: MuiButtonProps["color"] }
> = {
  primary: { variant: "contained", color: "inherit" },
  secondary: { variant: "outlined", color: "inherit" },
  tertiary: { variant: "text", color: "inherit" },
  neutral: { variant: "outlined", color: "inherit" },
  ghost: { variant: "text", color: "inherit" },
  danger: { variant: "contained", color: "error" },
};

const sizeMap: Record<ButtonSize, MuiButtonProps["size"]> = {
  sm: "small",
  md: "medium",
  lg: "large",
};

/** Match MUI's default icon sizing per button size */
const spinnerSizeMap: Record<ButtonSize, number> = {
  sm: 18,
  md: 20,
  lg: 22,
};

/** Per-variant style overrides applied as `sx` on the underlying MuiButton. */
function buildOverrideSx(variant: ButtonVariant): SxProps<Theme> {
  const sx: Record<string, unknown> = {};

  // Restore the error palette for `danger` (otherwise MUI's `contained`
  // styling would dominate).
  if (variant === "danger") {
    sx["&:not(.Mui-disabled)"] = {
      background: (t: Theme) => t.palette.error.main,
      "&:hover": {
        background: (t: Theme) => t.palette.error.dark,
      },
    };
  }

  // Contained brand button: flat purple ground, white label. `backgroundImage`
  // is cleared because the legacy theme paints a gradient through the
  // `background` shorthand, and a background image would otherwise render on
  // top of the colour set here.
  if (variant === "primary") {
    sx["&:not(.Mui-disabled)"] = {
      backgroundColor: brand("primary"),
      backgroundImage: "none",
      color: brand("onPrimary"),
      "&:hover": {
        backgroundColor: brand("primaryHover"),
        backgroundImage: "none",
      },
    };
  }

  // Outlined and text brand buttons: purple label, purple outline.
  if (variant === "secondary" || variant === "tertiary") {
    sx["&:not(.Mui-disabled)"] = {
      color: brand("primary"),
      ...(variant === "secondary" && {
        borderColor: brand("primary"),
      }),
      "&:hover": {
        color: brand("primaryHover"),
      },
    };
  }

  // Tertiary disabled state: muted brand purple instead of MUI's grey.
  if (variant === "tertiary") {
    sx["&.Mui-disabled"] = {
      color: brand("primary"),
      opacity: 0.4,
    };
  }

  // Neutral outlined: divider border + body text color, distinct from
  // the secondary palette outline. Suited for toolbars / muted actions.
  if (variant === "neutral") {
    sx["borderColor"] = (t: Theme) => t.palette.divider;
    sx["color"] = (t: Theme) => t.palette.text.primary;
    sx["&:hover"] = {
      borderColor: (t: Theme) => t.palette.text.secondary,
      backgroundColor: (t: Theme) => t.palette.action.hover,
    };
  }

  return sx;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      disabled = false,
      type,
      sx,
      startIcon,
      children,
      ...rest
    },
    ref,
  ) => {
    const { variant: muiVariant, color: muiColor } = variantMap[variant];
    const muiSize = sizeMap[size];
    const isButton = !rest.href && !rest.component;
    const overrideSx = buildOverrideSx(variant);

    return (
      <MuiButton
        data-testid="ds-button"
        ref={ref}
        variant={muiVariant}
        color={muiColor}
        size={muiSize}
        startIcon={
          loading ? (
            <CircularProgress size={spinnerSizeMap[size]} color="inherit" />
          ) : (
            startIcon
          )
        }
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        type={isButton ? (type ?? "button") : type}
        sx={[overrideSx, sx] as SxProps<Theme>}
        {...rest}
      >
        {children}
      </MuiButton>
    );
  },
);

Button.displayName = "Button";
